import {
	BRAND_COLOR_CATEGORIES,
	type BrandAsset,
	type BrandFontFile,
	type BrandLogoVariant,
	type BrandSwatch,
	type BrandTypeface,
} from './types';

/**
 * `getBrand` → the shapes the sections are drawn from.
 *
 * ── Why this lives in the package ──
 *
 * The rest of the package takes no GraphQL types on purpose, and this doesn't
 * break that: the input is described STRUCTURALLY below, so nothing here
 * imports `@codezo/graphql` and the package still builds on its own. The
 * generated `GetBrandQuery['getBrand']` satisfies these interfaces by shape.
 *
 * It lives here rather than in each app because it is the one piece of this
 * page that is genuinely identical in both — "a file of type logo is a logo
 * variant", "the four palette buckets flatten in this order" — and two copies
 * of that is how the old page ended up with two sets of rules for the same
 * data. Anything that legitimately differs between admin and org (what you may
 * edit, where a link goes) is a prop, not a branch in here.
 */

/* ── the input, by shape ── */

interface SourceColor {
	/* Nullable: `BrandColor.name` is `String`, not `String!`. A colour with no
	   name falls back to its hex at the point of display rather than being
	   dropped — an unnamed colour is still in the palette. */
	name?: string | null;
	hex: string;
	usage?: string | null;
	/** The print spec. Structural, like everything else here — it matches the
	 * generated `BrandPantoneRef` by shape. */
	pantone?: {
		book: string;
		code: string;
		hex?: string | null;
		color?: {
			bookTitle?: string | null;
			finish?: string | null;
			pageNumberInBook?: string | null;
		} | null;
	} | null;
}

interface SourceFile {
	id: string;
	name: string;
	url?: string | null;
	thumbnailUrl?: string | null;
	type: string;
	mimeType: string;
	size: number;
	uploadedAt?: string | null;
	designVariant?: string | null;
	backgroundType?: readonly string[] | null;
	usageContext?: readonly string[] | null;
	tags?: readonly string[] | null;
}

export interface BrandSource {
	name: string;
	description?: string | null;
	website?: string | null;
	logo?: {
		id: string;
		name: string;
		url?: string | null;
		thumbnailUrl?: string | null;
		mimeType: string;
	} | null;
	colorPalette?: {
		primary?: readonly SourceColor[] | null;
		secondary?: readonly SourceColor[] | null;
		accent?: readonly SourceColor[] | null;
		neutral?: readonly SourceColor[] | null;
	} | null;
	brandPersonality?: {
		tone?: string | null;
		traits?: readonly string[] | null;
		values?: readonly string[] | null;
	} | null;
	typography?: {
		primaryFont?: string | null;
		secondaryFont?: string | null;
		guidelines?: string | null;
	} | null;
	brandFiles?: readonly SourceFile[] | null;
	fontFiles?:
		| readonly {
				id: string;
				family: string;
				name: string;
				style?: string | null;
				weight?: string | null;
				url?: string | null;
		  }[]
		| null;
}

/** `BrandFileType.logo`, as the string the API sends. Compared rather than
 * imported so the package stays off the generated enum. */
const LOGO_TYPE = 'logo';

/* ── colors ── */

/**
 * The palette, flattened in category order.
 *
 * `index` is the position WITHIN a category, not in this list, because that is
 * what an update has to address: the palette is replaced whole and swatches
 * carry no id, so a delete is "drop item 2 of secondary". Flattening without
 * keeping that would make every edit hit the wrong color.
 */
export function toSwatches(source: BrandSource): BrandSwatch[] {
	const palette = source.colorPalette;
	if (!palette) return [];

	const out: BrandSwatch[] = [];
	for (const category of BRAND_COLOR_CATEGORIES) {
		const colors = palette[category] ?? [];
		colors.forEach((color, index) => {
			out.push({
				name: color.name ?? '',
				hex: color.hex,
				usage: color.usage ?? null,
				pantone: color.pantone ?? null,
				category,
				index,
			});
		});
	}
	return out;
}

/**
 * Every distinct color in the palette, in category order.
 *
 * The hero paints ALL of them. Three rules for picking two colors out of N all
 * failed on real kits — "next in flat order" gave two near-identical tints of
 * one blue, "first from another category" did nothing when the whole palette
 * sat in `primary`, and both kept landing on the neutral grey every kit
 * carries. Ranking by colorfulness fixed those cases and would have kept
 * inventing new ones, because any rule that discards colors has to justify
 * which. Using the whole palette needs no justification and cannot pick wrong.
 *
 * Duplicates are dropped: a hex repeated across buckets would otherwise add a
 * stop that flattens a stretch of the band for no reason.
 */
export function paletteHexes(source: BrandSource): string[] {
	const palette = source.colorPalette;
	if (!palette) return [];

	const seen = new Set<string>();
	const out: string[] = [];
	for (const category of BRAND_COLOR_CATEGORIES) {
		for (const color of palette[category] ?? []) {
			const key = color.hex.trim().toLowerCase();
			if (seen.has(key)) continue;
			seen.add(key);
			out.push(color.hex);
		}
	}
	return out;
}

/**
 * The single color the brand is represented by where a gradient won't do — the
 * `brand` logo-preview background, and the mark's own tile.
 */
export function primaryHex(source: BrandSource): string | null {
	return paletteHexes(source)[0] ?? null;
}

/* ── files ── */

/**
 * The logo variants.
 *
 * The brand's `logo` field and its logo-type FILES are two different things
 * that usually overlap: `logo` is the one chosen mark, the files are every
 * variant. A `logo` that has no matching file is prepended rather than dropped
 * — it is the brand's actual mark, and a logo section missing the logo is the
 * worst version of this page.
 */
export function toLogoVariants(source: BrandSource): BrandLogoVariant[] {
	const files = (source.brandFiles ?? []).filter((f) => f.type === LOGO_TYPE);
	const primaryId = source.logo?.id;

	const variants: BrandLogoVariant[] = files.map((file) => ({
		id: file.id,
		name: file.name,
		url: file.url,
		thumbnailUrl: file.thumbnailUrl,
		mimeType: file.mimeType,
		size: file.size,
		variantLabel: file.designVariant,
		backgrounds: file.backgroundType ? [...file.backgroundType] : null,
		isPrimary: Boolean(primaryId && file.id === primaryId),
	}));

	const logo = source.logo;
	if (logo && !files.some((f) => f.id === logo.id)) {
		variants.unshift({
			id: logo.id,
			name: logo.name,
			url: logo.url,
			thumbnailUrl: logo.thumbnailUrl,
			mimeType: logo.mimeType,
			size: 0,
			variantLabel: 'Primary mark',
			backgrounds: null,
			isPrimary: true,
		});
	}

	/* The chosen mark leads the grid; the rest keep upload order. */
	return variants.sort((a, b) => Number(b.isPrimary ?? false) - Number(a.isPrimary ?? false));
}

/** Everything that isn't a logo, as the asset list. */
export function toAssets(source: BrandSource): BrandAsset[] {
	return (source.brandFiles ?? [])
		.filter((f) => f.type !== LOGO_TYPE)
		.map((file) => ({
			id: file.id,
			name: file.name,
			url: file.url,
			thumbnailUrl: file.thumbnailUrl,
			mimeType: file.mimeType,
			size: file.size,
			uploadedAt: file.uploadedAt,
			category: file.type,
			tags: file.tags ? [...file.tags] : null,
		}));
}

export function toFontFiles(source: BrandSource): BrandFontFile[] {
	return (source.fontFiles ?? []).map((file) => ({
		id: file.id,
		family: file.family,
		name: file.name,
		style: file.style,
		weight: file.weight,
		url: file.url,
	}));
}

/* ── typography ── */

/**
 * The named faces, matched against the font files that back them.
 *
 * `hasFiles` is what separates "we know the brand uses Canela" from "a designer
 * can set Canela here" — two states the old page couldn't tell apart, because
 * it showed the files and never the names. Matching is case-insensitive on the
 * family, which is how the same face arrives spelled two ways.
 */
export function toTypefaces(source: BrandSource): BrandTypeface[] {
	const typography = source.typography;
	const fonts = source.fontFiles ?? [];

	const named: { role: string; family: string }[] = [];
	if (typography?.primaryFont) named.push({ role: 'Primary', family: typography.primaryFont });
	if (typography?.secondaryFont)
		named.push({ role: 'Secondary', family: typography.secondaryFont });

	/* A brand with font files but no `typography` still gets specimens — the
	   families are right there in the files, and one card per family beats a
	   section that says nothing while holding six fonts. */
	if (named.length === 0) {
		const families = Array.from(new Set(fonts.map((f) => f.family)));
		families.forEach((family, i) =>
			named.push({ role: i === 0 ? 'Primary' : `Face ${i + 1}`, family })
		);
	}

	return named.map(({ role, family }) => {
		const matching = fonts.filter(
			(f) => f.family.trim().toLowerCase() === family.trim().toLowerCase()
		);
		const styles = Array.from(
			new Set(
				matching
					.map((f) => [f.style, f.weight].filter(Boolean).join(' '))
					.filter((s) => s.length > 0)
			)
		);
		return { role, family, styles, hasFiles: matching.length > 0 };
	});
}
